import React, { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../store/authStore';
import { useNavigate } from 'react-router-dom';
import { scanAPI } from '../services/api';
import { userHasAnyRole } from '../utils/roles';
import {
  Camera,
  QrCode,
  Zap,
  X,
  CheckCircle,
  AlertCircle,
  Maximize2
} from 'lucide-react';
import './QRScannerPage.css';

const QRScannerPage = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [cameraActive, setCameraActive] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [lastScanResult, setLastScanResult] = useState(null);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const allowed = userHasAnyRole(user, ['STAFF', 'ADMIN', 'PLANNER']);
    if (!allowed) {
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => () => stopCamera(), []);

  const startCamera = async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Prefer back camera
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setCameraActive(true);
        // Add a slight delay to ensure video element isn't black when we display it
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          startScanning();
        };
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Could not access camera. Please ensure permissions are granted.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
    setScanning(false);
  };

  const startScanning = () => {
    if (!cameraActive || scanning) return;
    setScanning(true);
    requestAnimationFrame(scanForQRCode);
  };

  const scanForQRCode = () => {
    if (!scanning || !videoRef.current || !canvasRef.current) return;
    if (!processing && cameraActive) {
      // Placeholder for actual frame analysis
      // In a real implementation, you'd use a library like jsQR here
      // const code = jsQR(...) 
      // if (code) processQRCode(code.data);
    }
    if (scanning) {
      requestAnimationFrame(scanForQRCode);
    }
  };

  const processQRCode = async (codeData) => {
    if (!codeData || processing) return;
    setProcessing(true);
    setError(null);

    // Play a "beep" if possible
    playConfirmationTone();

    try {
      const response = await scanAPI.scanByQRCode(codeData, user?.id);
      setLastScanResult({ success: true, ...response });
    } catch (err) {
      setLastScanResult({ success: false, message: err?.message || 'Invalid Ticket' });
      setError(err?.message || 'Unable to valid ticket');
    } finally {
      setProcessing(false);
      // Optional: Stop scanning briefly after result
      setScanning(false);
    }
  };

  const playConfirmationTone = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      osc.start();
      osc.stop(ctx.currentTime + 0.1);
    } catch (e) {
      console.error("Audio play failed", e);
    }
  };

  const handleManualScan = async (e) => {
    e.preventDefault();
    if (!manualCode.trim()) return;
    await processQRCode(manualCode.trim());
    setManualCode('');
  };

  const clearResult = () => {
    setLastScanResult(null);
    setError(null);
    if (cameraActive) setScanning(true);
  };

  return (
    <div className="qr-scanner-shell">
      <div className="qr-content-wrapper">
        {/* Header */}
        <div className="qr-header-card">
          <div className="qr-header-title">
            <h1><QrCode size={28} className="text-blue-500" /> TikPlat Scan</h1>
            <p>Scan entry tickets or process manual check-ins.</p>
          </div>
          <div className="qr-status-group">
            <span className={`qr-status-pill ${cameraActive ? 'active' : ''}`}>
              <Camera size={14} /> {cameraActive ? 'Camera Ready' : 'Camera Off'}
            </span>
            <span className={`qr-status-pill ${processing ? 'busy' : ''}`}>
              <Zap size={14} /> {processing ? 'Processing...' : 'Ready'}
            </span>
          </div>
        </div>

        <div className="qr-grid-layout">
          <div className="qr-left-col" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {/* Scanner Card */}
            <div className="qr-section-card">
              <div className="qr-card-heading">
                <div>
                  <h2>Live Scanner</h2>
                  <p>Point camera at the QR code</p>
                </div>
              </div>

              {cameraActive ? (
                <>
                  <div className="qr-video-stage">
                    <video ref={videoRef} playsInline muted autoPlay />
                    <div className="qr-scan-overlay" />
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                  </div>
                  <div className="qr-action-group">
                    <button className="qr-control-btn danger" onClick={stopCamera}>
                      <X size={18} /> Stop
                    </button>
                  </div>
                </>
              ) : (
                <div className="qr-empty-state">
                  <Camera size={48} />
                  <div>
                    <h3>Camera Inactive</h3>
                    <p>Tap below to start scanning tickets</p>
                  </div>
                  <div className="qr-action-group" style={{ width: '100%' }}>
                    <button className="qr-control-btn primary" onClick={startCamera}>
                      <Camera size={18} /> Enable Camera
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="qr-right-col" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {/* Result Card */}
            {(lastScanResult || error) && (
              <div className="qr-result-card">
                <div className="qr-result-header">
                  <div className={`qr-result-badge ${lastScanResult?.success ? 'success' : 'error'}`}>
                    {lastScanResult?.success ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
                    {lastScanResult?.success ? 'Entry Authorized' : 'Entry Denied'}
                  </div>
                  <button className="qr-status-pill" onClick={clearResult} style={{ cursor: 'pointer' }}>
                    Close
                  </button>
                </div>

                {lastScanResult?.message && (
                  <p style={{ color: lastScanResult.success ? '#fff' : '#f87171', margin: '0 0 1rem 0' }}>
                    {lastScanResult.message}
                  </p>
                )}

                {lastScanResult?.ticket && (
                  <div className="qr-result-details">
                    <div className="qr-detail-row">
                      <span className="qr-detail-label">Ticket #</span>
                      <span className="qr-detail-value">{lastScanResult.ticket.ticketNumber}</span>
                    </div>
                    <div className="qr-detail-row">
                      <span className="qr-detail-label">Holder</span>
                      <span className="qr-detail-value">{lastScanResult.ticket.holderName || 'N/A'}</span>
                    </div>
                    <div className="qr-detail-row">
                      <span className="qr-detail-label">Event</span>
                      <span className="qr-detail-value">{lastScanResult.ticket.eventName}</span>
                    </div>
                    <div className="qr-detail-row">
                      <span className="qr-detail-label">Seat</span>
                      <span className="qr-detail-value">{lastScanResult.ticket.seatLabel || 'GA'}</span>
                    </div>
                  </div>
                )}
                {!lastScanResult && error && (
                  <p style={{ color: '#f87171' }}>{error}</p>
                )}
                <div style={{ marginTop: '1rem' }}>
                  <button className="qr-control-btn primary" onClick={clearResult} style={{ width: '100%' }}>
                    Scan Next Ticket
                  </button>
                </div>
              </div>
            )}

            {/* Manual Entry */}
            <div className="qr-section-card">
              <div className="qr-card-heading">
                <h2>Manual Entry</h2>
              </div>
              <form className="qr-manual-form" onSubmit={handleManualScan}>
                <input
                  className="qr-input"
                  placeholder="Enter Ticket ID or Code..."
                  value={manualCode}
                  onChange={e => setManualCode(e.target.value)}
                />
                <button className="qr-control-btn success" type="submit" disabled={!manualCode.trim() || processing}>
                  <Maximize2 size={18} /> Validate
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRScannerPage;
